import { PhysioLayout } from "@/components/layout/physio-layout";
import { useState } from "react";
import { ExternalLink, BookOpen, Search } from "lucide-react";

interface GuidelineResource {
  id: string;
  title: string;
  organisation: string;
  orgShort: string;
  category: string;
  year: string;
  summary: string;
  url: string;
  type: "guideline" | "resource" | "framework";
}

const GUIDELINES: GuidelineResource[] = [
  {
    id: "nice-ng65",
    title: "Spondyloarthritis in over 16s: diagnosis and management",
    organisation: "National Institute for Health and Care Excellence",
    orgShort: "NICE",
    category: "axSpA",
    year: "2017 (updated 2023)",
    summary: "Core NICE guideline for axSpA. Recommends supervised exercise as a cornerstone of management alongside NSAIDs. Physiotherapy should include spinal mobility exercises and aerobic conditioning. BASDAI ≥ 4 threshold for biologic consideration.",
    url: "https://www.nice.org.uk/guidance/ng65",
    type: "guideline",
  },
  {
    id: "nice-ng100",
    title: "Rheumatoid arthritis in adults: management",
    organisation: "National Institute for Health and Care Excellence",
    orgShort: "NICE",
    category: "RA",
    year: "2018 (updated 2023)",
    summary: "Recommends dynamic exercise (aerobic and resistance) for all RA patients. Physiotherapy for joint protection, fatigue management, and functional rehabilitation. HAQ-DI as standard outcome measure.",
    url: "https://www.nice.org.uk/guidance/ng100",
    type: "guideline",
  },
  {
    id: "nice-ng226",
    title: "Osteoarthritis in over 16s: diagnosis and management",
    organisation: "National Institute for Health and Care Excellence",
    orgShort: "NICE",
    category: "OA",
    year: "2022",
    summary: "Muscle-strengthening exercise and aerobic activity are core OA treatments. Physiotherapy-delivered education, exercise, and weight management are first-line. ESCAPE-pain and equivalent group programmes endorsed.",
    url: "https://www.nice.org.uk/guidance/ng226",
    type: "guideline",
  },
  {
    id: "nice-cg156",
    title: "Lupus (systemic lupus erythematosus)",
    organisation: "National Institute for Health and Care Excellence",
    orgShort: "NICE",
    category: "SLE",
    year: "2023",
    summary: "Multidisciplinary management including physiotherapy for fatigue, pain, and deconditioning. Recommends addressing cardiovascular risk through exercise. Signposts fatigue management interventions.",
    url: "https://www.nice.org.uk/guidance/ng206",
    type: "guideline",
  },
  {
    id: "nice-ph44",
    title: "Physical activity: brief advice for adults in primary care",
    organisation: "National Institute for Health and Care Excellence",
    orgShort: "NICE",
    category: "General",
    year: "2013",
    summary: "Recommends 150 min/week moderate aerobic activity for adults. Baseline physical activity guidance referenced by all rheumatology exercise prescriptions.",
    url: "https://www.nice.org.uk/guidance/ph44",
    type: "guideline",
  },
  {
    id: "csp-standards",
    title: "Quality Standards for Physiotherapy Practice",
    organisation: "Chartered Society of Physiotherapy",
    orgShort: "CSP",
    category: "Professional",
    year: "2021",
    summary: "The CSP Quality Standards define the professional and clinical standards expected of registered physiotherapists. Covers clinical assessment, outcome measurement, documentation, and clinical governance obligations.",
    url: "https://www.csp.org.uk/publications/quality-standards-physiotherapy-practice",
    type: "framework",
  },
  {
    id: "csp-aquatic",
    title: "Aquatic Physiotherapy Guidance",
    organisation: "Chartered Society of Physiotherapy",
    orgShort: "CSP",
    category: "Hydrotherapy",
    year: "2020",
    summary: "CSP guidance on aquatic physiotherapy competencies, safety requirements, pool standards, patient assessment, contraindications, and governance. Essential reading for physiotherapists delivering hydrotherapy.",
    url: "https://www.csp.org.uk/publications/aquatic-therapy-guidance",
    type: "framework",
  },
  {
    id: "csp-msk-pathway",
    title: "Physiotherapy Works: Musculoskeletal",
    organisation: "Chartered Society of Physiotherapy",
    orgShort: "CSP",
    category: "MSK",
    year: "2022",
    summary: "Evidence briefing on physiotherapy for musculoskeletal conditions. Includes inflammatory arthritis, OA, chronic pain, and rehabilitation. Summarises the evidence base for exercise and physiotherapy-led interventions.",
    url: "https://www.csp.org.uk/publications/physiotherapy-works-musculoskeletal-conditions",
    type: "resource",
  },
  {
    id: "acpr-standards",
    title: "ACPR Standards of Practice for Rheumatology Physiotherapy",
    organisation: "Association of Chartered Physiotherapists in Rheumatology",
    orgShort: "ACPR",
    category: "Professional",
    year: "2023",
    summary: "ACPR defines specialist competencies and standards for rheumatology physiotherapy practice in the UK. Covers assessment, exercise prescription, hydrotherapy, patient education, and multidisciplinary working. Essential reference for UK rheumatology physiotherapists.",
    url: "https://acpr.org.uk",
    type: "framework",
  },
  {
    id: "acpr-exercise",
    title: "ACPR Exercise Guidance for Inflammatory Arthritis",
    organisation: "Association of Chartered Physiotherapists in Rheumatology",
    orgShort: "ACPR",
    category: "Exercise",
    year: "2022",
    summary: "Condition-specific exercise guidance from the specialist rheumatology physiotherapy association. Covers axSpA, RA, SLE, myopathy, and SSc. Integrates BASEX and other validated exercise programmes.",
    url: "https://acpr.org.uk",
    type: "resource",
  },
  {
    id: "eular-pa",
    title: "EULAR Recommendations for Physical Activity in Inflammatory Arthritis and OA",
    organisation: "European Alliance of Associations for Rheumatology",
    orgShort: "EULAR",
    category: "Exercise",
    year: "2018",
    summary: "EULAR recommends physical activity as a standard component of care for all inflammatory arthritis patients. Evidence-based recommendations covering exercise type, intensity, and supervision requirements.",
    url: "https://www.eular.org",
    type: "guideline",
  },
  {
    id: "eular-axspa",
    title: "EULAR Recommendations for the Management of Axial Spondyloarthritis",
    organisation: "European Alliance of Associations for Rheumatology",
    orgShort: "EULAR",
    category: "axSpA",
    year: "2022",
    summary: "Non-pharmacological treatment is the foundation of axSpA management. Education and supervised exercise/physiotherapy are mandatory. Hydrotherapy endorsed where supervised exercise is not possible.",
    url: "https://www.eular.org",
    type: "guideline",
  },
  {
    id: "bsr-axspa",
    title: "BSR Guidelines on the Management of Axial Spondyloarthritis in Adults",
    organisation: "British Society for Rheumatology",
    orgShort: "BSR",
    category: "axSpA",
    year: "2021",
    summary: "BSR guidelines recommending physiotherapy as first-line treatment in axSpA. Exercise to include spinal mobility, aerobic conditioning, and hydrotherapy as appropriate. BASEX programme referenced.",
    url: "https://www.rheumatology.org.uk/practice-quality/guidelines",
    type: "guideline",
  },
  {
    id: "bsr-ra",
    title: "BSR and BHPR Guidelines for the Management of Rheumatoid Arthritis",
    organisation: "British Society for Rheumatology",
    orgShort: "BSR",
    category: "RA",
    year: "2020",
    summary: "Physiotherapy is recommended as part of the multidisciplinary management of RA. Exercise, hand therapy, and functional rehabilitation are first-line non-pharmacological interventions.",
    url: "https://www.rheumatology.org.uk/practice-quality/guidelines",
    type: "guideline",
  },
  {
    id: "hcpc-standards",
    title: "Standards of Proficiency — Physiotherapists",
    organisation: "Health and Care Professions Council",
    orgShort: "HCPC",
    category: "Professional",
    year: "2023",
    summary: "The HCPC Standards of Proficiency set the minimum standards required for HCPC registration. Physiotherapists must demonstrate safe and effective practice within these standards. Essential for CPD planning and revalidation.",
    url: "https://www.hcpc-uk.org/standards/standards-of-proficiency/physiotherapists/",
    type: "framework",
  },
];

const CATEGORIES = ["All", "axSpA", "RA", "SLE", "OA", "MSK", "Exercise", "Hydrotherapy", "Professional", "General"];
const ORGS = ["All", "NICE", "CSP", "ACPR", "EULAR", "BSR", "HCPC"];

const TYPE_COLOURS: Record<string, string> = {
  guideline: "bg-blue-100 text-blue-700",
  resource: "bg-emerald-100 text-emerald-700",
  framework: "bg-purple-100 text-purple-700",
};

const ORG_COLOURS: Record<string, string> = {
  NICE: "bg-slate-900 text-white",
  CSP: "bg-emerald-700 text-white",
  ACPR: "bg-teal-700 text-white",
  EULAR: "bg-blue-700 text-white",
  BSR: "bg-indigo-700 text-white",
  HCPC: "bg-orange-700 text-white",
};

export default function PhysioGuidelines() {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [orgFilter, setOrgFilter] = useState("All");

  const filtered = GUIDELINES.filter((g) => {
    const matchSearch = !search || g.title.toLowerCase().includes(search.toLowerCase()) || g.summary.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === "All" || g.category === categoryFilter;
    const matchOrg = orgFilter === "All" || g.orgShort === orgFilter;
    return matchSearch && matchCat && matchOrg;
  });

  return (
    <PhysioLayout title="CSP & NICE Guidelines">
      <div className="space-y-6">
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="h-5 w-5 text-emerald-600" />
            <h2 className="font-bold text-slate-900">Guidelines & Resources for Rheumatology Physiotherapy</h2>
          </div>
          <p className="text-sm text-slate-600">Curated collection of CSP guidance, NICE clinical guidelines, ACPR standards, BSR recommendations, and EULAR guidelines relevant to UK rheumatology physiotherapy practice. All links open the original source documents.</p>
        </div>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search guidelines..."
              className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button key={c} onClick={() => setCategoryFilter(c)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${categoryFilter === c ? "bg-emerald-700 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-emerald-300"}`}>
                  {c}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ORGS.map((o) => (
                <button key={o} onClick={() => setOrgFilter(o)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${orgFilter === o ? "bg-slate-700 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                  {o}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="text-sm text-slate-500">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</div>

        <div className="space-y-3">
          {filtered.map((g) => (
            <a
              key={g.id}
              href={g.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:border-emerald-200 transition-all group"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ORG_COLOURS[g.orgShort] ?? "bg-slate-100 text-slate-600"}`}>{g.orgShort}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${TYPE_COLOURS[g.type]}`}>{g.type}</span>
                    <span className="text-xs text-slate-400">{g.year}</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{g.category}</span>
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2 group-hover:text-emerald-700 transition-colors">{g.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{g.summary}</p>
                </div>
                <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-emerald-600 shrink-0 mt-1 transition-colors" />
              </div>
            </a>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <BookOpen className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p>No guidelines match your search</p>
            </div>
          )}
        </div>
      </div>
    </PhysioLayout>
  );
}
